import type { ProgrammeWorkspace } from "../../api.js";

type Session = ProgrammeWorkspace["sessions"][number];

type SessionCandidate = {
  dayId: string;
  roomId: string;
  startsAt: string;
  endsAt: string;
  chairs: string[];
  editingSessionId?: string;
};

type PresentationCandidate = {
  sessionId: string;
  startsAt: string;
  endsAt: string;
  presenters: string[];
  editingSlotId?: string;
};

const normalized = (values: string[]) => new Map(
  values.map((value) => [value.trim().toLocaleLowerCase(), value.trim()] as const).filter(([key]) => key),
);

const overlaps = (leftStart: number, leftEnd: number, rightStart: number, rightEnd: number) =>
  leftStart < rightEnd && rightStart < leftEnd;

export function sessionConflicts(candidate: SessionCandidate, sessions: Session[]) {
  const startsAt = new Date(candidate.startsAt).getTime();
  const endsAt = new Date(candidate.endsAt).getTime();
  if (!candidate.startsAt || !candidate.endsAt || Number.isNaN(startsAt) || Number.isNaN(endsAt)) return [];
  if (endsAt <= startsAt) return ["The session must end after it starts."];
  const warnings: string[] = [];
  const chairs = normalized(candidate.chairs);
  let roomConflict = false;
  const conflictingChairs = new Map<string, string>();
  sessions.forEach((session) => {
    if (session.id === candidate.editingSessionId || session.day_id !== candidate.dayId) return;
    const sessionStart = new Date(session.starts_at).getTime();
    const sessionEnd = new Date(session.ends_at).getTime();
    if (!overlaps(startsAt, endsAt, sessionStart, sessionEnd)) return;
    if (session.room_id === candidate.roomId) roomConflict = true;
    normalized(session.chairs ?? []).forEach((displayName, key) => {
      if (chairs.has(key)) conflictingChairs.set(key, displayName);
    });
  });
  if (roomConflict) warnings.push("This room already has an overlapping session.");
  conflictingChairs.forEach((displayName) => warnings.push(`${displayName} is chairing another session at this time.`));
  return warnings;
}

export function presentationConflicts(candidate: PresentationCandidate, sessions: Session[]) {
  const session = sessions.find((item) => item.id === candidate.sessionId);
  if (!session) return [];
  const startsAt = new Date(candidate.startsAt).getTime();
  const endsAt = new Date(candidate.endsAt).getTime();
  if (!candidate.startsAt || !candidate.endsAt || Number.isNaN(startsAt) || Number.isNaN(endsAt)) return [];
  if (endsAt <= startsAt) return ["The presentation must end after it starts."];
  const warnings: string[] = [];
  if (startsAt < new Date(session.starts_at).getTime() || endsAt > new Date(session.ends_at).getTime()) {
    warnings.push("The presentation must fit inside the selected session.");
  }
  const presenters = normalized(candidate.presenters);
  let sessionOverlap = false;
  const conflictingPresenters = new Map<string, string>();
  sessions.forEach((item) => item.slots.forEach((slot) => {
    if (slot.id === candidate.editingSlotId) return;
    const slotStart = new Date(slot.starts_at).getTime();
    const slotEnd = new Date(slot.ends_at).getTime();
    if (!overlaps(startsAt, endsAt, slotStart, slotEnd)) return;
    if (item.id === candidate.sessionId) sessionOverlap = true;
    normalized(slot.presenters).forEach((displayName, key) => {
      if (presenters.has(key)) conflictingPresenters.set(key, displayName);
    });
  }));
  if (sessionOverlap) warnings.push("This time overlaps another presentation in the session.");
  conflictingPresenters.forEach((displayName) => warnings.push(`${displayName} is presenting elsewhere at this time.`));
  return warnings;
}

export function suggestPresentationWindow(session: Session) {
  const sessionStart = new Date(session.starts_at).getTime();
  const sessionEnd = new Date(session.ends_at).getTime();
  const lastEnd = session.slots.reduce((latest, slot) => Math.max(latest, new Date(slot.ends_at).getTime()), sessionStart);
  const startsAt = Math.min(Math.max(sessionStart, lastEnd), sessionEnd);
  const endsAt = Math.min(startsAt + 20 * 60_000, sessionEnd);
  return { startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString() };
}
